"use client";

import { DEMO_CALENDAR_LINK } from "@/lib/constants/booking";
import LogoWall from "@/app/(public)/shared/components/LogoWall";
import Testimonial from "@/app/(public)/shared/components/Testimonial";
import { UnifiedModal, useModalManager } from "@/app/(public)/shared/components/UnifiedModal";
import { Button } from "@/app/(public)/shared/ui/Button";
import Image from "next/image";
import peopleTeamGraphic from "@/app/(public)/shared/assets/graphics/people-team.jpg";
import { CheckCircle } from "lucide-react";

export default function PeopleTeams() {
  const { openModal, closeModal, isModalOpen } = useModalManager();

  return (
    <main>
      {/* Hero Section */}
      <section className="min-h-[calc(100vh-64px)] lg:h-[calc(100vh-64px)] py-8 mt-[64px]">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-16 h-full">
          <div className="flex flex-col justify-center gap-8 md:gap-16 lg:gap-24">
            <div className="flex flex-col gap-6">
              <h1 className="text-h1 text-landing-primary">Culture isn't built in a Slack channel.</h1>
              <p className="text-body-lg text-landing-secondary max-w-530">
                Planning a retreat, kickoff, or global meetup? Nowadays handles the heavy lifting—from venue sourcing to
                logistics—so you can focus on your people.
              </p>
              <div className="flex">
                <Button variant="primary" color="primary" size="md" onClick={() => openModal("demo")}>
                  Get Started
                </Button>
              </div>
            </div>

            {/* Static Logo Wall */}
            <LogoWall
              variant="small-static"
              type="people-teams-small"
              trustedByText="Trusted by world class companies"
              className="justify-start"
            />
          </div>
          <div className="flex items-center justify-center lg:h-full mt-8 lg:mt-0">
            <div className="w-full max-w-lg lg:max-w-none">
              <Image
                src={peopleTeamGraphic}
                alt="People teams solution illustration"
                className="w-full h-auto object-contain rounded-2xl"
                priority
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto">
          {/* Section Header */}
          <div className="mb-16">
            <h2 className="text-h2 text-landing-primary mb-6">Make every offsite a cultural milestone</h2>
            <p className="text-body-lg text-landing-secondary max-w-2xl">
              Everything you need to create meaningful team experiences that strengthen relationships and drive
              engagement.
            </p>
          </div>

          {/* Initial Features - converted to document style */}
          <div className="space-y-12 mb-20">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">End-to-end retreat planning support</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                From initial concept to post-event follow-up, we handle every detail of your team retreat. Our
                comprehensive approach ensures seamless execution while you focus on your team's experience and
                outcomes.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Compare destinations based on real travel data</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Make informed decisions with comprehensive destination insights and travel logistics. We provide
                detailed analysis of travel costs, accessibility, and venue options to help you choose the perfect
                location for your team.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Curated venues, activities, and team bonding options</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Access handpicked venues and activities designed to foster team connection and collaboration. From
                inspirational settings to engaging team-building experiences, we create environments that strengthen
                bonds and drive results.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Flat-fee pricing, no hidden costs</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Transparent pricing that fits your budget with no surprises or hidden fees. Our straightforward pricing
                model helps you plan confidently and allocate resources effectively for maximum impact.
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Team building and culture events</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Design engaging team-building activities, company retreats, and culture initiatives that strengthen
                relationships and boost morale across your organization. Create shared experiences that foster
                collaboration, innovation, and lasting connections among team members.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Employee engagement tracking</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Measure the impact of your events with comprehensive engagement analytics, feedback collection, and
                insights that help you optimize your culture initiatives. Track satisfaction, participation, and
                long-term engagement outcomes to continuously improve your programs.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Inclusive and accessible experiences</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Create events that welcome everyone with accessibility features, diverse activity options, and inclusive
                planning that considers all team members' needs and preferences. Build culture that celebrates diversity
                and ensures every employee can participate fully.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-12 sm:py-0">
        <div className="">
          <Testimonial variant="single" type="general" featuredIndex={2} title="Don't just take our word for it." />
        </div>
      </section>

      {/* Demo Modal */}
      <UnifiedModal
        type="cal"
        isOpen={isModalOpen("demo")}
        onClose={() => closeModal("demo")}
        title="People & Teams Solutions"
        subtitle="Schedule a call to learn how we can strengthen your team culture"
        size="lg"
        calLink={DEMO_CALENDAR_LINK}
        fallbackText="Book your team building demo"
      />
    </main>
  );
}
